import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'form',
    'paymentElement',
    'submitButton',
    'buttonText',
    'buttonSpinner',
    'errors',
    'success',
    'surchargeNotice'
  ]
  static values = {
    site: String,
    publishableKey: String,
    customerId: String
  }

  connect() {
    // Chargebee.js docs: "If you want to reinitialize Chargebee.js within the
    // same session, call tearDown() to clean up before calling init() again."
    // The cbInstance + components bag are singletons that refuse re-registration
    // of a component type, so we must fully reset before a fresh init().
    this.#tearDownChargebee()
    this.initializeChargebee()
  }

  disconnect() {
    if (this.paymentComponent) {
      try {
        // close() is the documented method; unmount() is used by some versions.
        if (typeof this.paymentComponent.close === 'function') {
          this.paymentComponent.close()
        } else if (typeof this.paymentComponent.unmount === 'function') {
          this.paymentComponent.unmount()
        }
      } catch (error) {
        console.warn('[Chargebee] Error closing payment component:', error)
      }
      this.paymentComponent = null
    }
    this.#tearDownChargebee()
  }

  #tearDownChargebee() {
    if (window.Chargebee && typeof window.Chargebee.tearDown === 'function') {
      try {
        window.Chargebee.tearDown()
      } catch (error) {
        console.warn('[Chargebee] Error tearing down Chargebee:', error)
      }
    }
  }

  async initializeChargebee() {
    try {
      const cbInstance = window.Chargebee.init({
        site: this.siteValue,
        publishableKey: this.publishableKeyValue
      })
      console.log('[Chargebee] Initialized new instance')

      const paymentIntent = await this.createPaymentIntent()
      console.log('[Chargebee] Payment intent created:', paymentIntent.id)

      this.components = cbInstance.components({
        locale: 'en',
        style: { theme: { appearance: 'light' } }
      })

      this.paymentComponent = this.components.create(
        'payment',
        {
          paymentIntent: paymentIntent,
          layout: { type: 'panel' },
          defaultPaymentMethodType: 'direct_debit',
          paymentMethods: { allowed: ['card', 'direct_debit'] }
        },
        {
          onError: this.onError.bind(this),
          onSuccess: this.onSuccess.bind(this),
          onPaymentMethodChange: (paymentMethod) => {
            console.log('[Chargebee] Payment method changed:', paymentMethod)
            this.handlePaymentMethodChange(paymentMethod)
          }
        }
      )

      await this.paymentComponent.mount('#payment-element')
      console.log('[Chargebee] Payment component loaded and mounted')
    } catch (error) {
      console.error('[Chargebee] Initialization error:', error)
      this.showError('Failed to initialize payment form. Please try again.')
    }
  }

  async createPaymentIntent() {
    const response = await fetch('/onboarding/payment_intents', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({})
    })

    if (!response.ok) {
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create payment intent')
      } else {
        const text = await response.text()
        console.error('[Chargebee] Non-JSON response:', text.substring(0, 200))
        throw new Error('Failed to create payment intent - server error')
      }
    }

    const result = await response.json()
    return result.payment_intent
  }

  async submit(event) {
    event.preventDefault()

    // Show loading state
    this.setLoadingState(true)
    this.hideMessages()

    try {
      // Confirm payment - this will call onSuccess or onError callbacks automatically
      console.log('[Chargebee] Confirming payment...')
      await this.paymentComponent.confirm()
    } catch (error) {
      console.error('[Chargebee] Authorization error:', error)
      this.showError(
        error.message || 'An error occurred while processing your payment method. Please try again.'
      )
      this.setLoadingState(false)
    }
  }

  onSuccess(paymentIntent) {
    console.log('[Chargebee] Payment authorized successfully:', paymentIntent.id)
    console.log('[Chargebee] Full payment intent object:', paymentIntent)

    // Create payment source from the authorized payment intent
    this.createPaymentSource(paymentIntent.id)
      .then(() => {
        this.showSuccess()
        this.completeSetup()
      })
      .catch((error) => {
        console.error('[Chargebee] Error creating payment source:', error)
        this.showError('Payment authorized but failed to save. Please contact support.')
        this.setLoadingState(false)
      })
  }

  onError(error) {
    console.error('[Chargebee] Payment error:', error)
    this.showError(
      error.message || 'An error occurred while processing your payment method. Please try again.'
    )
    this.setLoadingState(false)
  }

  handlePaymentMethodChange(paymentMethod) {
    if (!this.hasSurchargeNoticeTarget) return

    if (paymentMethod === 'card') {
      this.surchargeNoticeTarget.classList.remove('d-none')
    } else {
      this.surchargeNoticeTarget.classList.add('d-none')
    }
  }

  async createPaymentSource(paymentIntentId) {
    const response = await fetch('/onboarding/payment_sources', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        payment_intent_id: paymentIntentId
      })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to create payment source')
    }

    return await response.json()
  }

  completeSetup() {
    const mainForm = document.getElementById('payment-step-form')
    if (mainForm) {
      console.log('[Chargebee] Auto-submitting form to complete setup')
      // Use requestSubmit() to trigger form validation and Rails UJS/Turbo properly
      mainForm.requestSubmit()
    }
  }

  setLoadingState(loading) {
    this.submitButtonTarget.disabled = loading
    this.buttonTextTarget.textContent = loading
      ? 'Processing...'
      : 'Add Payment Method & Complete Setup'

    if (loading) {
      this.buttonSpinnerTarget.classList.remove('d-none')
    } else {
      this.buttonSpinnerTarget.classList.add('d-none')
    }
  }

  hideMessages() {
    this.errorsTarget.classList.add('d-none')
    this.successTarget.classList.add('d-none')
  }

  showSuccess() {
    this.successTarget.classList.remove('d-none')
    this.submitButtonTarget.classList.add('d-none')
  }

  showError(message) {
    this.errorsTarget.textContent = message
    this.errorsTarget.classList.remove('d-none')
  }
}
